/**
 * FIKO UNIFIED LOD FACADE
 * =======================
 * Consolidates 4 independent LOD systems behind a single API:
 *
 * 1. FikoAdvancedLOD      - Distance-based LOD with GPU decimation
 * 2. FikoStreamingLOD     - Progressive geometry loading with memory budgeting
 * 3. FikoTemporalLOD      - Render frequency based on importance/distance
 * 4. FikoViewDependentLOD - View-dependent + predictive loading for huge scenes
 *
 * AUTO strategy selects the right combination based on scene triangle count:
 *   < 100K   → AdvancedLOD only (simple, low overhead)
 *   100K-1M  → AdvancedLOD + TemporalLOD
 *   1M-10M   → ViewDependentLOD + TemporalLOD
 *   > 10M    → ViewDependentLOD + StreamingLOD + TemporalLOD
 *
 * All LOD subsystems are lazy-imported (dynamic import) to avoid loading
 * unused code at startup.
 */

import * as THREE from 'three';

// ============================================================================
// TYPES
// ============================================================================

export type LODStrategy = 'distance' | 'streaming' | 'temporal' | 'view' | 'auto';

export interface UnifiedLODConfig {
  /** Initial strategy. Default: 'auto' */
  strategy: LODStrategy;
  /** Memory budget in bytes for streaming/view-dependent. Default: 512 MB */
  memoryBudgetBytes: number;
  /** Target FPS for temporal LOD adaptation. Default: 60 */
  targetFPS: number;
  /** LOD bias for view-dependent (positive = higher quality). Default: 0 */
  lodBias: number;
  /** Enable smooth LOD transitions (geomorphing). Default: true */
  enableTransitions: boolean;
  /** Log strategy decisions to console. Default: false */
  debug: boolean;
}

export interface MeshRegistrationOptions {
  /** Override auto-strategy for this specific mesh */
  strategy?: LODStrategy;
  /** Importance hint 0-1 for temporal LOD. Default: 0.5 */
  importance?: number;
  /** Base distance for distance-based LOD levels. Default: 50 */
  baseDistance?: number;
  /** Pre-computed bounding sphere (avoids recomputation) */
  boundingSphere?: THREE.Sphere;
}

export interface UnifiedLODStats {
  strategy: LODStrategy;
  effectiveStrategies: string[];
  totalMeshes: number;
  sceneTriangles: number;
  activeTriangles: number;
  memoryUsedMB: number;
  memoryBudgetMB: number;
  subsystems: {
    advanced: { active: boolean; meshCount: number; stats: any } | null;
    streaming: { active: boolean; meshCount: number; stats: any } | null;
    temporal: { active: boolean; meshCount: number; stats: any } | null;
    viewDependent: { active: boolean; meshCount: number; stats: any } | null;
  };
}

// ============================================================================
// LAZY IMPORT TYPES (minimal interfaces for type safety before import)
// ============================================================================

interface IAdvancedLOD {
  setRenderer(renderer: THREE.WebGLRenderer): void;
  createAutoLOD(mesh: THREE.Mesh, id: string, baseDistance?: number): THREE.Group;
  updateLODs(camera: THREE.Camera): void;
  getStats(): { total: number; active: number; totalTriangles: number; activeTriangles: number };
  dispose(): void;
}

interface IStreamingLODManager {
  setCamera(camera: THREE.Camera): void;
  registerObject(objectId: number, baseGeometry: THREE.BufferGeometry, boundingSphere?: THREE.Sphere): void;
  unregisterObject(objectId: number): void;
  update(deltaTime: number): void;
  setMemoryBudget(maxBytes: number): void;
  getCurrentLODGeometry(objectId: number): THREE.BufferGeometry | null;
  getStats(): any;
  dispose(): void;
}

interface ITemporalLOD {
  registerObject(object: THREE.Object3D, importance?: number): void;
  unregisterObject(object: THREE.Object3D): void;
  setImportance(object: THREE.Object3D, importance: number): void;
  update(): THREE.Object3D[];
  resetFrameStats(): void;
  configure(config: any): void;
  getStats(): any;
  dispose(): void;
}

interface IViewDependentLOD {
  registerMesh(id: string, mesh: THREE.Mesh, baseGeometry?: THREE.BufferGeometry): void;
  unregisterMesh(id: string): void;
  update(deltaTime: number): void;
  setLODBias(bias: number): void;
  setMemoryBudget(bytes: number): void;
  forceUpdateAll(): void;
  getStats(): any;
  dispose(): void;
}

// ============================================================================
// MESH ENTRY
// ============================================================================

interface MeshEntry {
  id: string;
  mesh: THREE.Mesh;
  options: MeshRegistrationOptions;
  triangleCount: number;
  /** Which subsystems this mesh is registered with */
  registeredIn: Set<string>;
  /** Numeric ID for streaming LOD (requires number keys) */
  numericId: number;
}

// ============================================================================
// MAIN CLASS
// ============================================================================

export class FikoUnifiedLOD {
  /**
   * Strategy constants for external reference
   */
  static readonly Strategy: Record<string, LODStrategy> = {
    DISTANCE: 'distance',
    STREAMING: 'streaming',
    TEMPORAL: 'temporal',
    VIEW_DEPENDENT: 'view',
    AUTO: 'auto'
  } as const;

  // Dependencies
  private camera: THREE.Camera;
  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private config: UnifiedLODConfig;

  // Subsystem instances (lazily loaded)
  private advancedLOD: IAdvancedLOD | null = null;
  private streamingLOD: IStreamingLODManager | null = null;
  private temporalLOD: ITemporalLOD | null = null;
  private viewDependentLOD: IViewDependentLOD | null = null;

  // Loading promises (prevent duplicate imports)
  private advancedLODPromise: Promise<IAdvancedLOD> | null = null;
  private streamingLODPromise: Promise<IStreamingLODManager> | null = null;
  private temporalLODPromise: Promise<ITemporalLOD> | null = null;
  private viewDependentLODPromise: Promise<IViewDependentLOD> | null = null;

  // Mesh registry
  private meshes = new Map<string, MeshEntry>();
  private nextNumericId = 1;

  // Scene metrics
  private totalSceneTriangles = 0;

  // Current effective strategy
  private currentStrategy: LODStrategy;
  private effectiveStrategies: string[] = [];

  // Disposed flag
  private disposed = false;

  constructor(
    camera: THREE.Camera,
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    config?: Partial<UnifiedLODConfig>
  ) {
    this.camera = camera;
    this.renderer = renderer;
    this.scene = scene;

    this.config = {
      strategy: config?.strategy ?? 'auto',
      memoryBudgetBytes: config?.memoryBudgetBytes ?? 512 * 1024 * 1024,
      targetFPS: config?.targetFPS ?? 60,
      lodBias: config?.lodBias ?? 0,
      enableTransitions: config?.enableTransitions ?? true,
      debug: config?.debug ?? false
    };

    this.currentStrategy = this.config.strategy;
  }

  // ==========================================================================
  // LAZY IMPORTS
  // ==========================================================================

  private async ensureAdvancedLOD(): Promise<IAdvancedLOD> {
    if (this.advancedLOD) return this.advancedLOD;

    if (!this.advancedLODPromise) {
      this.advancedLODPromise = import('./FikoAdvancedLOD').then(mod => {
        const instance = new mod.FikoAdvancedLOD();
        instance.setRenderer(this.renderer);
        this.advancedLOD = instance;
        this.log('FikoAdvancedLOD loaded');
        return instance;
      });
    }

    return this.advancedLODPromise;
  }

  private async ensureStreamingLOD(): Promise<IStreamingLODManager> {
    if (this.streamingLOD) return this.streamingLOD;

    if (!this.streamingLODPromise) {
      this.streamingLODPromise = import('./FikoStreamingLOD').then(mod => {
        const instance = new mod.FikoStreamingLODManager();
        instance.setCamera(this.camera);
        instance.setMemoryBudget(this.config.memoryBudgetBytes);
        this.streamingLOD = instance;
        this.log('FikoStreamingLODManager loaded');
        return instance;
      });
    }

    return this.streamingLODPromise;
  }

  private async ensureTemporalLOD(): Promise<ITemporalLOD> {
    if (this.temporalLOD) return this.temporalLOD;

    if (!this.temporalLODPromise) {
      this.temporalLODPromise = import('./FikoTemporalLOD').then(mod => {
        const instance = new mod.FikoTemporalLOD(this.camera, this.scene, this.renderer);
        instance.configure({
          enabled: true,
          adaptiveEnabled: true
        });
        this.temporalLOD = instance;
        this.log('FikoTemporalLOD loaded');
        return instance;
      });
    }

    return this.temporalLODPromise;
  }

  private async ensureViewDependentLOD(): Promise<IViewDependentLOD> {
    if (this.viewDependentLOD) return this.viewDependentLOD;

    if (!this.viewDependentLODPromise) {
      this.viewDependentLODPromise = import('./FikoViewDependentLOD').then(mod => {
        const instance = new mod.FikoViewDependentLOD(
          this.camera,
          this.renderer,
          this.scene,
          {
            maxMemoryBytes: this.config.memoryBudgetBytes,
            lodBias: this.config.lodBias,
            enableGeomorphing: this.config.enableTransitions,
            targetFrameTime: 1000 / this.config.targetFPS
          }
        );
        this.viewDependentLOD = instance;
        this.log('FikoViewDependentLOD loaded');
        return instance;
      });
    }

    return this.viewDependentLODPromise;
  }

  // ==========================================================================
  // STRATEGY RESOLUTION
  // ==========================================================================

  /**
   * Determine which subsystems to activate based on AUTO strategy
   */
  private resolveAutoStrategy(): string[] {
    const tri = this.totalSceneTriangles;

    if (tri < 100_000) {
      return ['advanced'];
    } else if (tri < 1_000_000) {
      return ['advanced', 'temporal'];
    } else if (tri < 10_000_000) {
      return ['viewDependent', 'temporal'];
    } else {
      return ['viewDependent', 'streaming', 'temporal'];
    }
  }

  /**
   * Resolve explicit strategy to subsystem names
   */
  private resolveExplicitStrategy(strategy: LODStrategy): string[] {
    switch (strategy) {
      case 'distance':
        return ['advanced'];
      case 'streaming':
        return ['streaming'];
      case 'temporal':
        return ['temporal'];
      case 'view':
        return ['viewDependent'];
      case 'auto':
        return this.resolveAutoStrategy();
      default:
        return ['advanced'];
    }
  }

  /**
   * Get the effective subsystem list for the current strategy
   */
  private getEffectiveSubsystems(): string[] {
    return this.resolveExplicitStrategy(this.currentStrategy);
  }

  // ==========================================================================
  // PUBLIC API
  // ==========================================================================

  /**
   * Register a mesh for unified LOD management.
   * Routes to appropriate subsystem(s) based on current strategy.
   */
  async registerMesh(
    id: string,
    mesh: THREE.Mesh,
    options?: MeshRegistrationOptions
  ): Promise<void> {
    if (this.disposed) return;

    const opts: MeshRegistrationOptions = {
      importance: 0.5,
      baseDistance: 50,
      ...options
    };

    // Count triangles
    const geometry = mesh.geometry as THREE.BufferGeometry;
    const triangleCount = geometry.index
      ? geometry.index.count / 3
      : (geometry.getAttribute('position')?.count ?? 0) / 3;

    const entry: MeshEntry = {
      id,
      mesh,
      options: opts,
      triangleCount,
      registeredIn: new Set(),
      numericId: this.nextNumericId++
    };

    this.meshes.set(id, entry);
    this.totalSceneTriangles += triangleCount;

    // Determine which subsystems to use
    const strategy = opts.strategy ?? this.currentStrategy;
    const subsystems = this.resolveExplicitStrategy(strategy);
    this.effectiveStrategies = subsystems;

    // Register in each subsystem
    const registrations: Promise<void>[] = [];

    if (subsystems.includes('advanced')) {
      registrations.push(this.registerInAdvanced(entry));
    }

    if (subsystems.includes('streaming')) {
      registrations.push(this.registerInStreaming(entry));
    }

    if (subsystems.includes('temporal')) {
      registrations.push(this.registerInTemporal(entry));
    }

    if (subsystems.includes('viewDependent')) {
      registrations.push(this.registerInViewDependent(entry));
    }

    await Promise.all(registrations);
  }

  /**
   * Unregister a mesh from all LOD subsystems
   */
  unregisterMesh(id: string): void {
    const entry = this.meshes.get(id);
    if (!entry) return;

    this.totalSceneTriangles -= entry.triangleCount;

    // Unregister from each subsystem
    if (entry.registeredIn.has('advanced') && this.advancedLOD) {
      // FikoAdvancedLOD does not expose individual unregister; handled via dispose
    }

    if (entry.registeredIn.has('streaming') && this.streamingLOD) {
      this.streamingLOD.unregisterObject(entry.numericId);
    }

    if (entry.registeredIn.has('temporal') && this.temporalLOD) {
      this.temporalLOD.unregisterObject(entry.mesh);
    }

    if (entry.registeredIn.has('viewDependent') && this.viewDependentLOD) {
      this.viewDependentLOD.unregisterMesh(id);
    }

    this.meshes.delete(id);
  }

  /**
   * Update all active LOD subsystems.
   * Call this every frame.
   */
  update(deltaTime: number): void {
    if (this.disposed) return;

    // Recalculate effective strategies if in auto mode
    if (this.currentStrategy === 'auto') {
      const newStrategies = this.resolveAutoStrategy();
      if (newStrategies.join(',') !== this.effectiveStrategies.join(',')) {
        this.log(`AUTO strategy changed: [${this.effectiveStrategies}] → [${newStrategies}]`);
        this.effectiveStrategies = newStrategies;
        // Re-registration of meshes into new subsystems would be needed for
        // a full dynamic switch; for now, new meshes will use the updated strategy.
      }
    }

    // Update each active subsystem
    if (this.advancedLOD) {
      this.advancedLOD.updateLODs(this.camera);
    }

    if (this.streamingLOD) {
      this.streamingLOD.update(deltaTime);
    }

    if (this.temporalLOD) {
      this.temporalLOD.resetFrameStats();
      this.temporalLOD.update();
    }

    if (this.viewDependentLOD) {
      this.viewDependentLOD.update(deltaTime);
    }
  }

  /**
   * Change the active LOD strategy.
   * Existing meshes remain in their current subsystems;
   * newly registered meshes will use the new strategy.
   * To migrate all meshes, call `reRegisterAll()` after.
   */
  setStrategy(strategy: LODStrategy): void {
    if (this.currentStrategy === strategy) return;

    this.log(`Strategy changed: ${this.currentStrategy} → ${strategy}`);
    this.currentStrategy = strategy;
    this.effectiveStrategies = this.resolveExplicitStrategy(strategy);
  }

  /**
   * Re-register all meshes with the current strategy.
   * Useful after a strategy change to migrate existing meshes.
   */
  async reRegisterAll(): Promise<void> {
    const entries = Array.from(this.meshes.values());

    // Unregister all from current subsystems
    for (const entry of entries) {
      this.unregisterMesh(entry.id);
    }

    // Re-register all with new strategy
    for (const entry of entries) {
      await this.registerMesh(entry.id, entry.mesh, entry.options);
    }
  }

  /**
   * Get consolidated statistics from all active subsystems
   */
  getStats(): UnifiedLODStats {
    let activeTriangles = this.totalSceneTriangles;
    let memoryUsedMB = 0;

    const advancedStats = this.advancedLOD ? this.advancedLOD.getStats() : null;
    const streamingStats = this.streamingLOD ? this.streamingLOD.getStats() : null;
    const temporalStats = this.temporalLOD ? this.temporalLOD.getStats() : null;
    const viewDependentStats = this.viewDependentLOD ? this.viewDependentLOD.getStats() : null;

    if (advancedStats) {
      activeTriangles = advancedStats.activeTriangles;
    }

    if (viewDependentStats) {
      memoryUsedMB = viewDependentStats.memoryUsedMB ?? 0;
    } else if (streamingStats) {
      // Parse memory string like "123.45 MB"
      const memStr = streamingStats.memoryUsed;
      if (typeof memStr === 'string') {
        memoryUsedMB = parseFloat(memStr) || 0;
      }
    }

    // Count meshes per subsystem
    let advancedCount = 0;
    let streamingCount = 0;
    let temporalCount = 0;
    let viewDependentCount = 0;

    for (const entry of this.meshes.values()) {
      if (entry.registeredIn.has('advanced')) advancedCount++;
      if (entry.registeredIn.has('streaming')) streamingCount++;
      if (entry.registeredIn.has('temporal')) temporalCount++;
      if (entry.registeredIn.has('viewDependent')) viewDependentCount++;
    }

    return {
      strategy: this.currentStrategy,
      effectiveStrategies: [...this.effectiveStrategies],
      totalMeshes: this.meshes.size,
      sceneTriangles: this.totalSceneTriangles,
      activeTriangles,
      memoryUsedMB,
      memoryBudgetMB: this.config.memoryBudgetBytes / (1024 * 1024),
      subsystems: {
        advanced: this.advancedLOD
          ? { active: true, meshCount: advancedCount, stats: advancedStats }
          : null,
        streaming: this.streamingLOD
          ? { active: true, meshCount: streamingCount, stats: streamingStats }
          : null,
        temporal: this.temporalLOD
          ? { active: true, meshCount: temporalCount, stats: temporalStats }
          : null,
        viewDependent: this.viewDependentLOD
          ? { active: true, meshCount: viewDependentCount, stats: viewDependentStats }
          : null
      }
    };
  }

  /**
   * Dispose all subsystems and release resources
   */
  dispose(): void {
    this.disposed = true;

    if (this.advancedLOD) {
      this.advancedLOD.dispose();
      this.advancedLOD = null;
    }

    if (this.streamingLOD) {
      this.streamingLOD.dispose();
      this.streamingLOD = null;
    }

    if (this.temporalLOD) {
      this.temporalLOD.dispose();
      this.temporalLOD = null;
    }

    if (this.viewDependentLOD) {
      this.viewDependentLOD.dispose();
      this.viewDependentLOD = null;
    }

    this.advancedLODPromise = null;
    this.streamingLODPromise = null;
    this.temporalLODPromise = null;
    this.viewDependentLODPromise = null;

    this.meshes.clear();
    this.totalSceneTriangles = 0;
  }

  // ==========================================================================
  // SUBSYSTEM REGISTRATION HELPERS
  // ==========================================================================

  private async registerInAdvanced(entry: MeshEntry): Promise<void> {
    const lod = await this.ensureAdvancedLOD();
    lod.createAutoLOD(entry.mesh, entry.id, entry.options.baseDistance);
    entry.registeredIn.add('advanced');
  }

  private async registerInStreaming(entry: MeshEntry): Promise<void> {
    const lod = await this.ensureStreamingLOD();
    const geometry = entry.mesh.geometry as THREE.BufferGeometry;
    lod.registerObject(entry.numericId, geometry, entry.options.boundingSphere);
    entry.registeredIn.add('streaming');
  }

  private async registerInTemporal(entry: MeshEntry): Promise<void> {
    const lod = await this.ensureTemporalLOD();
    lod.registerObject(entry.mesh, entry.options.importance ?? 0.5);
    entry.registeredIn.add('temporal');
  }

  private async registerInViewDependent(entry: MeshEntry): Promise<void> {
    const lod = await this.ensureViewDependentLOD();
    lod.registerMesh(entry.id, entry.mesh);
    entry.registeredIn.add('viewDependent');
  }

  // ==========================================================================
  // UTILITY
  // ==========================================================================

  private log(message: string): void {
    if (this.config.debug) {
      console.log(`[FikoUnifiedLOD] ${message}`);
    }
  }
}

// ============================================================================
// SINGLETON FACTORY
// ============================================================================

let _unifiedLODInstance: FikoUnifiedLOD | null = null;

/**
 * Get or create the unified LOD singleton
 */
export function getUnifiedLOD(
  camera?: THREE.Camera,
  renderer?: THREE.WebGLRenderer,
  scene?: THREE.Scene,
  config?: Partial<UnifiedLODConfig>
): FikoUnifiedLOD | null {
  if (!_unifiedLODInstance && camera && renderer && scene) {
    _unifiedLODInstance = new FikoUnifiedLOD(camera, renderer, scene, config);
  }
  return _unifiedLODInstance;
}

/**
 * Dispose the unified LOD singleton
 */
export function disposeUnifiedLOD(): void {
  if (_unifiedLODInstance) {
    _unifiedLODInstance.dispose();
    _unifiedLODInstance = null;
  }
}
